import React, { useState, useEffect, useMemo } from "react";
import { History } from "lucide-react";
import { useQuery } from '@tanstack/react-query';
import { api } from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import moment from "moment";
import "moment/locale/fr";
import { SearchInput, FilterButton, ActiveFilters, ListLoader, ListEmpty, ListCard, thClass, Modal, ModalFooter } from "../../stock/StockUI";
import AppleSelect from "../Common/AppleSelect";
import { inputClass } from "../../ClientsTable/clientFormUI";
import { tdClass, ErrorLine } from "./technicalFilesUI";
import DateInput from "../Common/DateInput";

const fieldClass = inputClass(false);
const labelClass = "text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block";

moment.locale("fr");

// Tous les types de mouvement que RecipeStockEngine/RecipeStockMovement peuvent
// produire côté backend (voir clicoo RecipeStockMovement.php docblock) —
// libellé + couleur, pour ne jamais afficher un type inconnu comme "Vente" par défaut.
const MOVEMENT_TYPE_LABELS = {
    sale_consumption: { label: 'Vente', color: 'blue' },
    batch_production: { label: 'Production (ingrédients)', color: 'orange' },
    batch_yield: { label: 'Production (rendement)', color: 'green' },
    reversal_sale: { label: 'Remboursement', color: 'purple' },
    reversal_batch: { label: 'Annulation production', color: 'red' },
    override_adjustment: { label: 'Ajustement commande', color: 'gray' },
};

const movementBadgeClasses = (color) => ({
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
    green: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
    red: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
}[color] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400');

// « Recherche avancée » — edits a draft, applied only on « Appliquer »
const AdvancedSearchModal = ({ initial, recipes, products, onApply, onClose }) => {
    const [draft, setDraft] = useState(initial);
    const set = (key) => (e) => setDraft(prev => ({ ...prev, [key]: e.target.value }));
    const today = moment().format('YYYY-MM-DD');
    const invalidRange = draft.start && draft.end && draft.start > draft.end;

    return (
        <Modal
            title="Recherche avancée"
            subtitle="Affinez l'historique de déstockage"
            onClose={onClose}
            footer={
                <ModalFooter
                    cancelLabel="Réinitialiser"
                    onCancel={() => setDraft({ start: today, end: today, recipe: '', product: '' })}
                    onConfirm={() => onApply(draft)}
                    disabled={invalidRange}
                    label="Appliquer"
                />
            }
        >
            <div className="space-y-4">
                <div>
                    <label className={labelClass}>Période :</label>
                    <div className="grid grid-cols-2 gap-3">
                        <DateInput aria-label="Du" value={draft.start} max={draft.end || undefined}
                               onChange={set('start')} className={fieldClass} />
                        <DateInput aria-label="Au" value={draft.end} min={draft.start || undefined}
                               onChange={set('end')} className={fieldClass} />
                    </div>
                    {invalidRange && <p className="text-xs text-red-500 mt-1">La date de début doit précéder la date de fin.</p>}
                    <div className="flex flex-wrap gap-2 mt-2">
                        {[
                            { label: "Aujourd'hui", start: today, end: today },
                            { label: '7 derniers jours', start: moment().subtract(6, 'days').format('YYYY-MM-DD'), end: today },
                            { label: 'Ce mois-ci', start: moment().startOf('month').format('YYYY-MM-DD'), end: today },
                        ].map(p => {
                            const active = draft.start === p.start && draft.end === p.end;
                            return (
                                <button key={p.label} type="button"
                                        onClick={() => setDraft(prev => ({ ...prev, start: p.start, end: p.end }))}
                                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                                            active
                                                ? 'bg-primary/10 border-primary/30 text-primary-dark dark:text-primary'
                                                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-primary/30 hover:text-primary'}`}>
                                    {p.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div>
                    <label className={labelClass}>Recette :</label>
                    <AppleSelect value={draft.recipe} onChange={set('recipe')} className={fieldClass}>
                        <option value="">Toutes les recettes</option>
                        {recipes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </AppleSelect>
                </div>

                <div>
                    <label className={labelClass}>Produit :</label>
                    <AppleSelect value={draft.product} onChange={set('product')} className={fieldClass}>
                        <option value="">Tous les produits</option>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </AppleSelect>
                </div>
            </div>
        </Modal>
    );
};

const StockMovementsHistory = ({ isDarkMode }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: "selection",
        },
    ]);
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
    const [selectedRecipe, setSelectedRecipe] = useState('');
    const [selectedProduct, setSelectedProduct] = useState('');
    const [showFilterModal, setShowFilterModal] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchStockMovements = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            let url = '/recipes/stock-movements';
            const params = [];

            if (dateRange && dateRange.length > 0) {
                const range = dateRange[0];
                if (range.startDate) {
                    params.push(`startDate=${moment(range.startDate).format('YYYY-MM-DD')}`);
                }
                if (range.endDate) {
                    params.push(`endDate=${moment(range.endDate).format('YYYY-MM-DD')}`);
                }
            }

            if (debouncedSearchTerm) {
                params.push(`search=${encodeURIComponent(debouncedSearchTerm)}`);
            }

            if (selectedRecipe) {
                params.push(`recipe_id=${selectedRecipe}`);
            }

            if (selectedProduct) {
                params.push(`product_id=${selectedProduct}`);
            }

            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await api.get(url, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.detail || "Erreur lors de la récupération de l'historique");
        }
    };

    const { data: movementsData, isLoading, error } = useQuery({
        queryKey: ['stock-movements', dateRange, debouncedSearchTerm, selectedRecipe, selectedProduct],
        queryFn: fetchStockMovements,
        keepPreviousData: true
    });

    // Filtres Recette / Produit — la donnée existait déjà en state mais
    // n'avait jamais de <AppleSelect> pour la piloter (code mort avant Étape 6).
    const { data: recipesForFilter } = useQuery({
        queryKey: ['recipes-for-stock-filter'],
        queryFn: async () => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get('/recipes', {
                headers: { 'X-account-id': accountId.id },
                params: { itemsPerPage: 200 }
            });
            return response.data;
        }
    });

    const { data: productsForFilter } = useQuery({
        queryKey: ['products-for-stock-filter'],
        queryFn: async () => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get('/products', {
                headers: { 'X-account-id': accountId.id },
                params: { itemsPerPage: 200 }
            });
            return response.data;
        }
    });

    const sortedMovements = useMemo(() => {
        if (!movementsData?.data) return [];
        const sortable = [...movementsData.data];
        sortable.sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
            if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sortable;
    }, [movementsData, sortConfig]);


    const toDateStr = (d) => (d ? moment(d).format('YYYY-MM-DD') : '');
    const todayStr = toDateStr(new Date());
    const startStr = toDateStr(dateRange[0]?.startDate);
    const endStr = toDateStr(dateRange[0]?.endDate);

    const recipeOptions = recipesForFilter?.data || [];
    const productOptions = productsForFilter?.data || [];

    const applyFilters = ({ start, end, recipe, product }) => {
        setDateRange([{
            startDate: start ? new Date(start) : new Date(),
            endDate: end ? new Date(end) : new Date(),
            key: "selection"
        }]);
        setSelectedRecipe(recipe);
        setSelectedProduct(product);
        setShowFilterModal(false);
    };

    const resetPeriod = () => setDateRange([{ startDate: new Date(), endDate: new Date(), key: "selection" }]);
    const clearAllFilters = () => {
        resetPeriod();
        setSelectedRecipe('');
        setSelectedProduct('');
        setSearchTerm('');
    };

    // Period counts as a filter only when it differs from the default (today)
    const periodChanged = startStr !== todayStr || endStr !== todayStr;
    const chips = [
        searchTerm && { key: 'search', label: `Recherche: ${searchTerm}`, onRemove: () => setSearchTerm('') },
        periodChanged && {
            key: 'period',
            label: startStr === endStr
                ? `Le ${moment(startStr).format('DD/MM/YYYY')}`
                : `Du ${moment(startStr).format('DD/MM/YYYY')} au ${moment(endStr).format('DD/MM/YYYY')}`,
            onRemove: resetPeriod,
        },
        selectedRecipe && {
            key: 'recipe',
            label: `Recette: ${recipeOptions.find(r => r.id === selectedRecipe)?.name || '…'}`,
            onRemove: () => setSelectedRecipe(''),
        },
        selectedProduct && {
            key: 'product',
            label: `Produit: ${productOptions.find(p => p.id === selectedProduct)?.name || '…'}`,
            onRemove: () => setSelectedProduct(''),
        },
    ].filter(Boolean);
    const activeFilterCount = [periodChanged, selectedRecipe, selectedProduct].filter(Boolean).length;

    return (
        <div className="text-sm">
            {/* Toolbar: search + « Filtres », like the Stock history tab */}
            <div className="flex flex-col sm:flex-row">
                <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher un mouvement (recette, produit, employé)..." />
                <FilterButton count={activeFilterCount} onClick={() => setShowFilterModal(true)} />
            </div>
            <ActiveFilters chips={chips} onClearAll={clearAllFilters} />

            {showFilterModal && (
                <AdvancedSearchModal
                    initial={{ start: startStr, end: endStr, recipe: selectedRecipe, product: selectedProduct }}
                    recipes={recipeOptions}
                    products={productOptions}
                    onApply={applyFilters}
                    onClose={() => setShowFilterModal(false)}
                />
            )}

            {isLoading ? (
                <ListLoader label="Chargement de l'historique..." />
            ) : error ? (
                <ErrorLine message={error.message} />
            ) : (
                <ListCard>
                    {sortedMovements.length === 0 ? (
                        <ListEmpty icon={History} title="Aucun mouvement"
                                   text="Aucun mouvement de stock disponible pour cette période." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th className={thClass}>Date</th>
                                    <th className={thClass}>Recette</th>
                                    <th className={thClass}>Produit</th>
                                    <th className={`${thClass} text-right`}>Quantité</th>
                                    <th className={thClass}>Type</th>
                                    <th className={thClass}>Employé</th>
                                </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {sortedMovements.map((movement, index) => (
                                    <tr key={movement.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className={`${tdClass} tabular-nums text-gray-500 dark:text-gray-400`}>
                                            {moment(movement.created_at).format('DD/MM/YYYY HH:mm')}
                                        </td>
                                        <td className={`${tdClass} font-semibold text-gray-900 dark:text-gray-100`}>
                                            <div>{movement.recipe?.name || '-'}</div>
                                        </td>
                                        <td className={`${tdClass} text-gray-700 dark:text-gray-300`}>
                                            <div>{movement.product?.name || movement.variant?.name || '-'}</div>
                                        </td>
                                        <td className={`${tdClass} text-right tabular-nums font-medium text-gray-900 dark:text-gray-100`}>
                                            {movement.quantity_consumed}
                                        </td>
                                        <td className={tdClass}>
                                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${movementBadgeClasses(MOVEMENT_TYPE_LABELS[movement.movement_type]?.color)}`}>
                                                {MOVEMENT_TYPE_LABELS[movement.movement_type]?.label || movement.movement_type}
                                            </span>
                                        </td>
                                        <td className={`${tdClass} text-gray-700 dark:text-gray-300`}>
                                            {movement.employee_name || '-'}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ListCard>
            )}
        </div>
    );
};

export default StockMovementsHistory;
